import { useId, type ReactNode } from "react";

import { Button } from "@/design-system/components/ui";

import { combinedIntroCopy as copy } from "./combinedIntroCopy";
import type { CombinedIntroSelection } from "./combinedIntroSelection";

/**
 * The bar above a buyer's submissions while rows are ticked for a combined
 * intro email: how many, whether they came from more than one page, Clear, and
 * Send.
 *
 * <p>Absent at zero — there is nothing to say. At one, and above the maximum,
 * Send stays on screen DISABLED with the sentence saying why beside it, wired
 * with `aria-describedby` so the reason is read with the button rather than
 * found by accident. A reader who ticks one listing is told where the one-listing
 * email lives instead of being left to wonder why the button does nothing.</p>
 *
 * <p>No `onSend`, no Send button and no reason — a host that cannot open the
 * composer must not offer a button that does nothing.</p>
 */
export function CombinedIntroSelectionBar({
  selection,
  onSend,
  actions,
}: {
  selection: CombinedIntroSelection;
  onSend?: () => void;
  /** The bar's other bulk actions — ARCHIVE and, for an admin, DELETE (D3a). */
  actions?: ReactNode;
}) {
  const reasonId = useId();
  const { count, spansPages, sendBlockedReason, clear, introEligible } = selection;
  if (count === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid="combined-intro-bar"
    >
      <span className="text-small text-onyx" data-testid="combined-intro-bar-count">
        {copy.selectedCount(count)}
        {spansPages ? ` ${copy.onMoreThanOnePage}` : ""}
      </span>
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-space-2">
        {onSend && sendBlockedReason && (
          <span
            id={reasonId}
            className="text-caption text-gray-mid"
            data-testid="combined-intro-bar-reason"
          >
            {sendBlockedReason}
          </span>
        )}
        <Button variant="text" size="sm" onClick={clear} data-testid="combined-intro-bar-clear">
          {copy.clear}
        </Button>
        {actions}
        {onSend && (
          <Button
            size="sm"
            onClick={onSend}
            disabled={sendBlockedReason != null}
            aria-describedby={sendBlockedReason ? reasonId : undefined}
            data-testid="combined-intro-bar-send"
          >
            {copy.sendButton(introEligible.length)}
          </Button>
        )}
      </div>
    </div>
  );
}
